import { useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, signOut, useMe } from '../lib/api';
import { Fox } from './Fox';

/** Shows the sign-in screen while signed out, and a "no access" screen for a Google account that isn't on a team. */
export function SignInGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const qc = useQueryClient();
  useEffect(() => {
    const out = () => qc.invalidateQueries({ queryKey: ['me'] });
    window.addEventListener('coachrex:signed-out', out);
    return () => window.removeEventListener('coachrex:signed-out', out);
  }, [qc]);
  const err = me.error instanceof ApiError ? me.error : null;
  if (err?.code === 'SIGNED_OUT') return <SignIn />;
  if (err?.code === 'NO_ACCESS') return <NoAccess message={err.message} />;
  // Nothing loads until we know who is looking.
  if (me.isPending) return null;
  return <>{children}</>;
}

function SignIn() {
  const failed = new URLSearchParams(window.location.search).get('signin') === 'failed';
  return (
    <div className="signin">
      <div className="signin__card">
        <span className="signin__brand">
          <Fox size={40} ring />
          <span>
            Coach <span className="rex">Lex</span>
          </span>
        </span>
        <span className="signin__lead">Sign in with the Google account that uses your HighLevel email.</span>
        {failed && <span className="signin__error">Google sign-in didn’t finish. Try again.</span>}
        <a className="btn btn--primary signin__google" href="/auth/google">
          <GoogleG />
          Sign in with Google
        </a>
      </div>
    </div>
  );
}

function NoAccess({ message }: { message: string }) {
  return (
    <div className="signin">
      <div className="signin__card">
        <span className="signin__brand">
          <Fox size={40} ring />
          <span>
            Coach <span className="rex">Lex</span>
          </span>
        </span>
        <span className="signin__lead">{message}</span>
        <button className="btn btn--secondary" onClick={() => void signOut()}>
          Sign in with another account
        </button>
      </div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}
